import React, { useState, useRef, useEffect } from "react";

import i18n from "../../providers/i18n";

import thFlag from "../../assets/image/flag_th.svg";
import enFlag from "../../assets/image/flag_us.svg";

const LanguageToggle: React.FC = () => {
  const [open, setOpen] = useState(false);

  // Initialize language from localStorage or i18n
  const savedLang = localStorage.getItem("lang") || i18n.language;
  const [lang, setLang] = useState<string>(savedLang);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: "th", label: "ไทย (TH)", flag: thFlag },
    { code: "en", label: "English (EN)", flag: enFlag },
  ];

  const selected = languages.find((l) => l.code === lang);

  // ---- Change language ----
  const changeLanguage = (lng: string) => {
    if (lng !== lang) {
      i18n.changeLanguage(lng); // update i18n
      localStorage.setItem("lang", lng); // persist
      setLang(lng); // update toggle state
    }
    setOpen(false);
  };

  // ---- Sync state if language changes elsewhere ----
  useEffect(() => {
    const handleLanguageChange = (lng: string) => setLang(lng);
    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, []);

  // ---- Close dropdown when clicking outside ----
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="relative inline-block text-left">
      {/* Dropdown Button */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-md bg-white hover:bg-gray-100 transition"
      >
        <img
          src={selected?.flag}
          alt={selected?.label}
          className="w-5 h-5 rounded-sm"
        />
        <span className="text-sm font-medium">{selected?.code.toUpperCase()}</span>
        <svg
          className={`w-4 h-4 transition-transform ${
            open ? "rotate-180" : "rotate-0"
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {open && (
        <div className="absolute right-0 mt-2 w-36 bg-white border border-gray-200 rounded-md shadow-lg z-50">
          {languages.map((lng) => (
            <button
              key={lng.code}
              onClick={() => changeLanguage(lng.code)}
              className={`flex items-center w-full gap-2 px-3 py-2 text-sm hover:bg-gray-100 transition ${
                lang === lng.code ? "bg-blue-50 text-blue-600" : "text-gray-700"
              }`}
            >
              <img src={lng.flag} alt={lng.label} className="w-5 h-5 rounded-sm" />
              {lng.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageToggle;
